<template>
  <div class="rounded-lg border border-border">
    <div
      ref="area"
      class="group relative w-full touch-none rounded-lg outline-none"
      :class="$style.area"
      role="slider"
      tabindex="0"
      :aria-label="`Saturation and lightness`"
      :aria-valuenow="Math.round(saturation * 100)"
      aria-valuemin="0"
      aria-valuemax="100"
      :style="{
        backgroundColor: `hsl(${hue}, 100%, 50%)`,
      }"
      @pointerdown="onPointerDown"
      @keydown="onKeydown"
    >
      <div
        class="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white group-focus-visible:size-6 group-active:size-6"
        :class="$style.thumb"
        :style="{
          left: `${Math.max(0, Math.min(100, saturation * 100))}%`,
          top: `${Math.max(0, Math.min(100, (1 - lightness) * 100))}%`,
          backgroundColor: thumbColor,
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Hsl, convertHslToRgb, formatHex } from "culori/fn";
import { computed, useTemplateRef } from "vue";

const modelValue = defineModel<Hsl>({ required: true });

const area = useTemplateRef("area");

const hue = computed(() => modelValue.value.h ?? 0);
const saturation = computed(() => modelValue.value.s);
const lightness = computed(() => modelValue.value.l);

const thumbColor = computed(() => formatHex(convertHslToRgb(modelValue.value)));

function valuesFromPointer(event: PointerEvent) {
  const rect = area.value!.getBoundingClientRect();
  const s = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
  const l =
    1 - Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
  return { s, l };
}

function push(s: number, l: number) {
  modelValue.value = { ...modelValue.value, s, l };
}

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0) {
    return;
  }

  event.preventDefault();
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

  const { s, l } = valuesFromPointer(event);
  push(s, l);

  const onPointerMove = (e: PointerEvent) => {
    const { s, l } = valuesFromPointer(e);
    push(s, l);
  };

  const onPointerUp = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    area.value!.removeEventListener("pointermove", onPointerMove);
    area.value!.removeEventListener("pointerup", onPointerUp);
  };

  area.value!.addEventListener("pointermove", onPointerMove);
  area.value!.addEventListener("pointerup", onPointerUp);
}

const STEP = 0.01;
const PAGE_STEP = 0.1;

function onKeydown(event: KeyboardEvent) {
  const step = event.shiftKey ? PAGE_STEP : STEP;
  let ds = 0;
  let dl = 0;

  switch (event.key) {
    case "ArrowLeft":
      ds = -step;
      break;
    case "ArrowRight":
      ds = step;
      break;
    case "ArrowUp":
      dl = step;
      break;
    case "ArrowDown":
      dl = -step;
      break;
    default:
      return;
  }

  event.preventDefault();
  push(
    Math.max(0, Math.min(1, saturation.value + ds)),
    Math.max(0, Math.min(1, lightness.value + dl))
  );
}
</script>

<style module>
.area {
  aspect-ratio: 3/2;
  background-image: linear-gradient(to top, #000000, transparent, #ffffff),
    linear-gradient(to right, #808080, transparent);
}

.thumb {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: width 0.15s, height 0.15s;
}
</style>
